import React, { useState, useEffect } from 'react';
import { Icons } from '../Icons';
import { useLanguage } from '../../contexts/LanguageContext';
import { ConsentStorage } from '../../services/storage';

const Footer = ({ onPricingClick, onShowDataSourceInfo, showDataSourceInfo, apiStatus, onNavigateTo }) => {
  const currentYear = new Date().getFullYear();
  const [localShowDataSourceInfo, setLocalShowDataSourceInfo] = useState(false);
  const [navigation, setNavigation] = useState({ grouped: {} });
  const { t, language } = useLanguage();
  const isCs = language === 'cs';
  
  const isInfoVisible = showDataSourceInfo !== undefined ? showDataSourceInfo : localShowDataSourceInfo;
  const handleToggleInfo = () => {
    if (onShowDataSourceInfo) {
      onShowDataSourceInfo();
    } else {
      setLocalShowDataSourceInfo(!localShowDataSourceInfo);
    }
  };

  useEffect(() => {
    fetch('/api/navigation?location=footer')
      .then(r => r.json())
      .then(data => setNavigation(data))
      .catch(err => console.error('Failed to load footer navigation:', err));
  }, []);

  const footerSections = navigation.grouped?.footer || {};
  const localizedHref = (url) => {
    if (language !== 'en' || !url?.startsWith('/')) return url;
    const localized = new URL(url, window.location.origin);
    localized.searchParams.set('lang', 'en');
    return `${localized.pathname}${localized.search}${localized.hash}`;
  };

  const renderNavItem = (item) => {
    const label = isCs ? item.label_cs : item.label_en;
    return (
      <li key={item.id}>
        <a 
          href={localizedHref(item.url)}
          className="theme-link hover:text-cyan-400 transition-colors text-sm"
        >
          {label}
        </a>
      </li>
    );
  };

  return (
    <footer className="border-t theme-border bg-[var(--bg-surface)]/50 mt-12 relative z-10">
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 mb-12">
          <div>
            <h4 className="theme-text font-semibold mb-4 text-sm">{t('footer.product')}</h4>
            <ul className="space-y-2">
              <li>
                <a href={localizedHref('/pricing')} className="theme-link hover:text-cyan-400 transition-colors text-sm">
                  {t('footer.pricing')}
                </a>
              </li>
              {(footerSections['product'] || []).filter(item => item.url !== '/pricing' && item.url !== '#methodology').map(renderNavItem)}
              <li>
                <button onClick={handleToggleInfo} className="theme-link hover:text-cyan-400 transition-colors text-sm">
                  {t('footer.methodology')}
                </button>
              </li>
            </ul>
          </div>
          
          <div>
            <h4 className="theme-text font-semibold mb-4 text-sm">{t('footer.solutions')}</h4>
            <ul className="space-y-2">
              <li>
                <a 
                  href={localizedHref('/web-core-vitals-google-report-crux')}
                  onClick={(e) => {
                    if (!onNavigateTo || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                    e.preventDefault();
                    onNavigateTo('crux-alternativa', e.currentTarget.getAttribute('href'));
                  }}
                  className="text-cyan-400 hover:text-cyan-300 transition-colors text-sm font-medium"
                >
                  {t('footer.cruxAlternative')}
                </a>
              </li>
              {(footerSections['solutions'] || []).filter(item => item.url !== '/web-core-vitals-google-report-crux' && item.url !== '/top-performers').map(renderNavItem)}
            </ul>
          </div>
          
          <div>
            <h4 className="theme-text font-semibold mb-4 text-sm">{t('footer.legal')}</h4>
            <ul className="space-y-2">
              {(footerSections['legal'] || []).map(renderNavItem)}
              <li>
                <button 
                  onClick={() => ConsentStorage.clearConsent()} 
                  className="theme-link hover:text-cyan-400 transition-colors text-sm"
                >
                  {t('footer.cookieSettings') || 'Nastavení cookies'}
                </button>
              </li>
            </ul>
          </div>
          
          <div>
            <h4 className="theme-text font-semibold mb-4 text-sm">{t('footer.company')}</h4>
            <ul className="space-y-2">
              {(footerSections['company'] || []).map(renderNavItem)}
            </ul>
          </div>
        </div>
        
        {isInfoVisible && (
          <div className="mb-8 theme-card rounded-lg p-4 animate-slide-in">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-semibold theme-text">
                {t('footer.methodology')}
              </span>
              <button 
                onClick={handleToggleInfo}
                className="theme-text-secondary hover:theme-text transition-colors"
              >
                <Icons.Close size={16} />
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-3 text-xs">
              <div className="flex gap-3 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                <span className="text-lg">👤</span>
                <div>
                  <p className="font-medium text-green-400 mb-1">{t('footer.cruxMethodTitle')}</p>
                  <p className="theme-text-secondary">{t('footer.cruxMethodDesc')}</p>
                </div>
              </div>
              <div className="flex gap-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <span className="text-lg">⚙️</span>
                <div>
                  <p className="font-medium text-blue-400 mb-1">{t('footer.lighthouseMethodTitle')}</p>
                  <p className="theme-text-secondary">{t('footer.lighthouseMethodDesc')}</p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="pt-8 border-t theme-border">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-cyan-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                </svg>
                <span className="text-base font-bold tracking-tight">
                  <span className="text-white dark:text-white">Control</span>
                  <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Vitals</span>
                </span>
              </div>
              <span className="theme-text-muted text-sm">© {currentYear}</span>
            </div>
            
            <div className="text-center">
              <p className="theme-text-muted text-xs">
                {t('footer.dataFrom')}: <a href="https://developer.chrome.com/docs/crux" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400 underline">Chrome UX Report</a> (CC BY 4.0) • <a href="https://developers.google.com/speed/docs/insights" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400 underline">Google PageSpeed</a>
              </p>
            </div>
            
            {apiStatus && (
              <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs ${
                apiStatus.status === 'ok' 
                  ? 'bg-green-500/10 text-green-400 border border-green-500/30' 
                  : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/30'
              }`}>
                <div className={`w-1.5 h-1.5 rounded-full ${
                  apiStatus.status === 'ok' ? 'bg-green-400' : 'bg-yellow-400'
                }`}></div>
                <span>{apiStatus.apis?.pagespeed ? 'API Online' : 'API Offline'}</span>
              </div>
            )}
            {!apiStatus && (
              <span className="px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs">
                API Online
              </span>
            )}
          </div>
          
          <p className="mt-6 text-xs theme-text-muted text-center max-w-3xl mx-auto leading-relaxed">
            {t('footer.disclaimer')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
